import { useEffect, useState } from "react";
import { api, formatNaira, formatDate } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Chip, statusChip } from "@/components/Chip";
import { Button } from "@/components/ui/button";
import { Play } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from "@/components/ui/dialog";

export default function Payroll() {
  const [runs, setRuns] = useState([]);
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/payroll-runs").then(r => setRuns(r.data));
  useEffect(() => { load(); }, []);

  const runPayroll = async () => {
    const usersRes = await api.get("/users");
    const users = usersRes.data;
    const entries = users.map(u => ({
      user_id: u.id, name: u.name, role: u.role,
      gross: 450000, paye: 45000, pension: 36000, net: 369000,
    }));
    const now = new Date();
    await api.post("/payroll-runs", {
      month: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`,
      status: "pending",
      entries,
      total_gross: entries.reduce((s, e) => s + e.gross, 0),
      total_net: entries.reduce((s, e) => s + e.net, 0),
    });
    toast.success("Payroll run created");
    load();
  };

  const approve = async (id) => {
    await api.patch(`/payroll-runs/${id}`, { status: "approved" });
    toast.success("Approved");
    load();
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Payroll"
        subtitle="Monthly payroll runs and payslips"
        actions={
          <Button onClick={runPayroll} className="bg-brand-aqua hover:bg-brand-aqua-hover text-white" data-testid="run-payroll-btn">
            <Play className="w-4 h-4 mr-1" /> Run Payroll
          </Button>
        }
      />

      <div className="table-wrap">
        <table className="w-full text-sm">
          <thead className="table-head">
            <tr>
              <th className="text-left px-4 py-3">Month</th>
              <th className="text-right px-4 py-3">Total Gross</th>
              <th className="text-right px-4 py-3">Total Net</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {runs.map(r => (
              <tr key={r.id} className="row">
                <td className="px-4 py-3 font-medium num text-slate-800">{r.month}</td>
                <td className="px-4 py-3 text-right num">{formatNaira(r.total_gross)}</td>
                <td className="px-4 py-3 text-right font-heading font-semibold num">{formatNaira(r.total_net)}</td>
                <td className="px-4 py-3"><Chip variant={statusChip(r.status)}>{r.status}</Chip></td>
                <td className="px-4 py-3 text-right space-x-2">
                  <Button size="sm" variant="outline" onClick={() => { setActive(r); setOpen(true); }} data-testid={`view-payroll-${r.id}`}>View payslips</Button>
                  {r.status !== "approved" && <Button size="sm" onClick={() => approve(r.id)} className="bg-brand-aqua hover:bg-brand-aqua-hover text-white">Approve</Button>}
                </td>
              </tr>
            ))}
            {runs.length === 0 && <tr><td colSpan={5} className="text-center py-12 text-slate-500 text-sm">No payroll runs yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Payslips — {active?.month}</DialogTitle></DialogHeader>
          {active && (
            <table className="w-full text-sm">
              <thead className="table-head">
                <tr>
                  <th className="text-left px-3 py-2">Employee</th>
                  <th className="text-right px-3 py-2">Gross</th>
                  <th className="text-right px-3 py-2">PAYE</th>
                  <th className="text-right px-3 py-2">Pension</th>
                  <th className="text-right px-3 py-2">Net</th>
                </tr>
              </thead>
              <tbody>
                {(active.entries || []).map((e, i) => (
                  <tr key={e.user_id || e.email || `${e.name}-${i}`} className="border-t border-slate-100">
                    <td className="px-3 py-2 text-slate-800">{e.name}<div className="text-xs text-slate-500 capitalize">{e.role?.replace("_", " ")}</div></td>
                    <td className="px-3 py-2 text-right num">{formatNaira(e.gross)}</td>
                    <td className="px-3 py-2 text-right num text-red-500">{formatNaira(e.paye)}</td>
                    <td className="px-3 py-2 text-right num text-red-500">{formatNaira(e.pension)}</td>
                    <td className="px-3 py-2 text-right font-heading font-semibold num">{formatNaira(e.net)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
